import { useEffect, useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { supabaseBlogService } from '@/services/supabaseBlogService';
import { supabase } from '@/integrations/supabase/client';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { generateSlug, validateSlugUnique } from '@/utils/slugUtils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription } from '@/components/ui/form';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { RichTextEditor } from '@/components/admin/RichTextEditor';
import { useToast } from '@/hooks/use-toast';
import { Loader2, ArrowLeft, Save, Send, Trash2, Sparkles, Upload, X, CalendarIcon, Clock } from 'lucide-react';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';

const blogPostSchema = z.object({
  title: z.string().min(1, 'Title is required'),
  slug: z.string().min(1, 'Slug is required'),
  excerpt: z.string().optional(),
  content: z.string().min(1, 'Content is required'),
  author_name: z.string().default('SeniorThrive Team'),
  featured_image_url: z.string().optional(),
  featured_image_alt: z.string().optional(),
  categories: z.string().optional(),
  tags: z.string().optional(),
  status: z.enum(['draft', 'published', 'archived', 'scheduled']).default('draft'),
  seo_description: z.string().optional(),
  scheduled_publish_at: z.string().optional(),
});

type BlogPostFormData = z.infer<typeof blogPostSchema>;

export default function BlogPostEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { session, isLoading: authLoading } = useAdminAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(!!id);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [aiTopic, setAiTopic] = useState('');
  const [aiContext, setAiContext] = useState('');
  const [videoScript, setVideoScript] = useState<{
    topic: string;
    hook: string;
    body: string;
    callToAction: string;
    socialMediaPost?: string;
    hashtags?: string[];
    videoCaption?: string;
  } | null>(null);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const form = useForm<BlogPostFormData>({
    resolver: zodResolver(blogPostSchema),
    defaultValues: {
      title: '',
      slug: '',
      excerpt: '',
      content: '',
      author_name: 'SeniorThrive Team',
      featured_image_url: '',
      featured_image_alt: '',
      categories: '',
      tags: '',
      status: 'draft',
      seo_description: '',
      scheduled_publish_at: '',
    },
  });

  useEffect(() => {
    if (id) {
      fetchPost();
    }
  }, [id]);

  const fetchPost = async () => {
    if (!id) return;
    
    try {
      setIsFetching(true);
      const post = await supabaseBlogService.fetchPostById(id);
      
      if (!post) {
        toast({
          title: 'Error',
          description: 'Blog post not found',
          variant: 'destructive',
        });
        navigate('/admin/content');
        return;
      }

      form.reset({
        title: post.title,
        slug: post.slug,
        excerpt: post.excerpt || '',
        content: post.content,
        author_name: post.author_name,
        featured_image_url: post.featured_image_url || '',
        featured_image_alt: post.featured_image_alt || '',
        categories: post.categories?.join(', ') || '',
        tags: post.tags?.join(', ') || '',
        status: post.status,
        seo_description: post.seo_meta?.description || '',
        scheduled_publish_at: post.scheduled_publish_at || '',
      });

      // Load content package if it exists
      if (post.custom_fields?.contentPackage) {
        setVideoScript(post.custom_fields.contentPackage);
      }
    } catch (error) {
      console.error('Error fetching post:', error);
      toast({
        title: 'Error',
        description: 'Failed to fetch blog post',
        variant: 'destructive',
      });
    } finally {
      setIsFetching(false);
    }
  };

  // Extract plain text from HTML content
  const extractPlainText = (html: string): string => {
    const div = document.createElement('div');
    div.innerHTML = html;
    return div.textContent || div.innerText || '';
  };

  // Extract first meaningful paragraph from content
  const extractExcerpt = (html: string): string => {
    const div = document.createElement('div');
    div.innerHTML = html;
    
    // Find first paragraph with substantial content
    const paragraphs = div.querySelectorAll('p');
    for (const p of paragraphs) {
      const text = p.textContent?.trim() || '';
      if (text.length > 50) {
        return text.length > 200 ? text.slice(0, 197) + '...' : text;
      }
    }
    
    // Fallback to plain text extraction
    const plainText = extractPlainText(html).trim();
    return plainText.length > 200 ? plainText.slice(0, 197) + '...' : plainText;
  };

  // Generate SEO description (160 chars max)
  const generateSeoDescription = (html: string): string => {
    const plainText = extractPlainText(html).trim();
    return plainText.length > 157 ? plainText.slice(0, 154) + '...' : plainText;
  };

  const onTitleChange = async (title: string) => {
    form.setValue('title', title);
    if (!id) {
      const slug = generateSlug(title);
      const { isUnique, suggestedSlug } = await validateSlugUnique(slug, 'blog_posts');
      form.setValue('slug', isUnique ? slug : suggestedSlug || slug);
    }
    
    // Auto-generate image alt text from title
    if (title && !form.getValues('featured_image_alt')) {
      form.setValue('featured_image_alt', title);
    }
  };

  const onContentChange = (content: string) => {
    form.setValue('content', content);
    
    // Only auto-fill if fields are empty (don't override user edits)
    if (!form.getValues('excerpt')) {
      const excerpt = extractExcerpt(content);
      if (excerpt) {
        form.setValue('excerpt', excerpt);
      }
    }
    
    if (!form.getValues('seo_description')) {
      const seoDesc = generateSeoDescription(content);
      if (seoDesc) {
        form.setValue('seo_description', seoDesc);
      }
    }
  };

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      toast({
        title: 'Invalid File Type',
        description: 'Please upload a JPEG, PNG, WebP, or GIF image.',
        variant: 'destructive',
      });
      return;
    }

    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      toast({
        title: 'File Too Large',
        description: 'Please upload an image smaller than 10MB.',
        variant: 'destructive',
      });
      return;
    }

    try {
      setIsUploadingImage(true);
      
      // Generate unique filename
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`;
      const filePath = `blog/${fileName}`;

      // Upload to Supabase Storage
      const { data, error } = await supabase.storage
        .from('blog-images')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false,
        });

      if (error) {
        throw error;
      }

      // Get public URL
      const { data: urlData } = supabase.storage
        .from('blog-images')
        .getPublicUrl(filePath);

      form.setValue('featured_image_url', urlData.publicUrl);
      
      toast({
        title: 'Image Uploaded',
        description: 'Featured image uploaded successfully.',
      });
    } catch (error: any) {
      console.error('Error uploading image:', error);
      toast({
        title: 'Upload Failed',
        description: error.message || 'Failed to upload image. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsUploadingImage(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const onSubmit = async (data: BlogPostFormData, status: 'draft' | 'published' | 'scheduled') => {
    try {
      if (!session) {
        console.error('No session found when attempting to save');
        toast({
          title: 'Authentication Required',
          description: 'You must be logged in to save blog posts. Please sign in again.',
          variant: 'destructive',
        });
        navigate('/admin/login');
        return;
      }

      console.log('Starting blog post save with status:', status);
      console.log('Session user:', session.user?.id);

      console.log('Refreshing session before save...');
      const { data: { session: refreshedSession }, error: refreshError } = await supabase.auth.refreshSession();
      
      if (refreshError) {
        console.error('Session refresh error:', refreshError);
        toast({
          title: 'Session Error',
          description: 'Failed to refresh authentication. Please sign in again.',
          variant: 'destructive',
        });
        navigate('/admin/login');
        return;
      }
      
      if (!refreshedSession) {
        console.error('No session after refresh');
        toast({
          title: 'Authentication Required',
          description: 'Session expired. Please sign in again.',
          variant: 'destructive',
        });
        navigate('/admin/login');
        return;
      }
      
      console.log('Session refreshed successfully:', {
        userId: refreshedSession.user.id,
        email: refreshedSession.user.email,
        tokenPreview: refreshedSession.access_token?.substring(0, 20) + '...'
      });

      setIsLoading(true);

      const categories = data.categories?.split(',').map(c => c.trim()).filter(Boolean) || [];
      const tags = data.tags?.split(',').map(t => t.trim()).filter(Boolean) || [];

      const postData: any = {
        title: data.title,
        slug: data.slug,
        excerpt: data.excerpt,
        content: data.content,
        author_name: data.author_name,
        featured_image_url: data.featured_image_url,
        featured_image_alt: data.featured_image_alt,
        categories,
        tags,
        status,
        seo_meta: { description: data.seo_description },
        custom_fields: videoScript ? { contentPackage: videoScript } : undefined,
        scheduled_publish_at: status === 'scheduled' && data.scheduled_publish_at ? data.scheduled_publish_at : null,
      };

      console.log('Saving blog post data:', { ...postData, content: postData.content?.substring(0, 50) + '...' });

      if (id) {
        await supabaseBlogService.updatePost(id, postData);
        toast({
          title: 'Success',
          description: 'Blog post updated successfully',
        });
      } else {
        await supabaseBlogService.createPost(postData);
        toast({
          title: 'Success',
          description: 'Blog post created successfully',
        });
      }

      navigate('/admin/content');
    } catch (error: any) {
      console.error('Detailed error saving post:', {
        error,
        message: error.message,
        code: error.code,
        details: error.details,
        hint: error.hint
      });
      
      if (error.code === 'PGRST301' || 
          error.code === '42501' || 
          error.message?.includes('row-level security') || 
          error.message?.includes('JWT') ||
          error.message?.includes('permission denied')) {
        toast({
          title: 'Authentication Error',
          description: 'Your session may have expired or you lack permissions. Please sign out and sign in again.',
          variant: 'destructive',
        });
      } else {
        toast({
          title: 'Error',
          description: `Failed to save blog post: ${error.message || 'Unknown error'}. Check console for details.`,
          variant: 'destructive',
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!id) return;

    try {
      setIsLoading(true);
      await supabaseBlogService.deletePost(id);
      toast({
        title: 'Success',
        description: 'Blog post deleted successfully',
      });
      navigate('/admin/content');
    } catch (error) {
      console.error('Error deleting post:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete blog post',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
      setShowDeleteDialog(false);
    }
  };


  const handleAIGenerate = async () => {
    if (!aiTopic.trim()) {
      toast({
        title: 'Topic Required',
        description: 'Please enter a topic for the blog post',
        variant: 'destructive',
      });
      return;
    }

    try {
      setIsGenerating(true);
      
      const { data, error } = await supabase.functions.invoke('generate-blog-post', {
        body: { 
          topic: aiTopic,
          additionalContext: aiContext || undefined
        },
      });

      if (error) {
        console.error('Error generating blog post:', error);
        throw error;
      }

      if (!data) {
        throw new Error('No data returned from AI');
      }

      console.log('Setting form values from AI generation:', {
        contentLength: data.content?.length,
        hasImage: !!data.featured_image_url,
        hasContentPackage: !!data.contentPackage,
      });

      form.setValue('title', data.title || '');
      form.setValue('slug', data.slug || '');
      form.setValue('excerpt', data.excerpt || '');
      form.setValue('content', data.content || '');
      form.setValue('categories', data.categories?.join(', ') || '');
      form.setValue('seo_description', data.seo_meta?.description || '');
      
      if (data.featured_image_url) {
        form.setValue('featured_image_url', data.featured_image_url);
        form.setValue('featured_image_alt', data.featured_image_alt || '');
      }

      // Store content package if generated
      if (data.contentPackage) {
        setVideoScript(data.contentPackage);
      }
      
      setTimeout(() => {
        console.log('Form values after AI generation:', {
          content: form.getValues('content')?.substring(0, 50),
          image: form.getValues('featured_image_url')?.substring(0, 50),
        });
      }, 100);

      const successMessages = ['Blog post generated successfully'];
      if (data.featured_image_url) successMessages.push('featured image');
      if (data.contentPackage) successMessages.push('complete content package');

      toast({
        title: 'Success!',
        description: `${successMessages[0]}${successMessages.length > 1 ? ' with ' + successMessages.slice(1).join(' and ') : ''}. Review and publish when ready.`,
      });

      setAiTopic('');
      setAiContext('');
    } catch (error: any) {
      console.error('Error generating blog post:', error);
      
      let errorMessage = 'Failed to generate blog post';
      if (error.message?.includes('Rate limit')) {
        errorMessage = 'Rate limit exceeded. Please try again in a moment.';
      } else if (error.message?.includes('credits')) {
        errorMessage = 'AI credits depleted. Please add credits to your workspace.';
      }
      
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setIsGenerating(false);
    }
  };

  if (isFetching || authLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="container mx-auto py-8 max-w-4xl">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/admin/content')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <h1 className="text-3xl font-bold">{id ? 'Edit' : 'New'} Blog Post</h1>
        </div>
        {id && (
          <Button variant="destructive" onClick={() => setShowDeleteDialog(true)}>
            <Trash2 className="h-4 w-4 mr-2" />
            Delete
          </Button>
        )}
      </div>

      {!id && (
        <Card className="mb-6 border-primary/20">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-primary" />
              <CardTitle>AI Blog Post Generator</CardTitle>
            </div>
            <CardDescription>
              Let Gemini AI write your blog post! Just provide a topic and we'll generate a complete, 
              SEO-optimized blog post aligned with SeniorThrive's voice and mission.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Topic *</label>
              <Input
                placeholder="e.g., Fall prevention tips for seniors at home"
                value={aiTopic}
                onChange={(e) => setAiTopic(e.target.value)}
                disabled={isGenerating}
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">Additional Context (optional)</label>
              <Textarea
                placeholder="Any specific points or angles you want to cover..."
                value={aiContext}
                onChange={(e) => setAiContext(e.target.value)}
                rows={3}
                disabled={isGenerating}
              />
            </div>
            <Button
              onClick={handleAIGenerate}
              disabled={isGenerating || !aiTopic.trim()}
              className="w-full"
            >
              {isGenerating ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Generating Blog Post...
                </>
              ) : (
                <>
                  <Sparkles className="h-4 w-4 mr-2" />
                  Generate Blog Post with AI
                </>
              )}
            </Button>
          </CardContent>
        </Card>
      )}

      {videoScript && (
        <Card className="mb-6 border-purple-200 bg-purple-50/50 dark:bg-purple-950/20">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-purple-600" />
                <CardTitle className="text-purple-900 dark:text-purple-100">Complete Content Package Generated</CardTitle>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setVideoScript(null)}>
                Dismiss
              </Button>
            </div>
            <CardDescription>
              Blog post, video script, social media content, and more - ready to publish
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Video Script Section */}
            <div className="border-b pb-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-bold text-lg text-purple-900 dark:text-purple-100">📹 Video Script (30-45 sec)</h3>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const scriptText = `Topic: ${videoScript.topic}\n\nHook:\n${videoScript.hook}\n\nBody:\n${videoScript.body}\n\nCall to Action:\n${videoScript.callToAction}`;
                    navigator.clipboard.writeText(scriptText);
                    toast({
                      title: 'Copied!',
                      description: 'Video script copied to clipboard',
                    });
                  }}
                >
                  Copy Script
                </Button>
              </div>
              <div className="space-y-3 text-sm">
                <div>
                  <h4 className="font-semibold text-purple-900 dark:text-purple-100 mb-1">Hook (with visual cue):</h4>
                  <p className="whitespace-pre-wrap bg-white/50 dark:bg-black/20 p-2 rounded">{videoScript.hook}</p>
                </div>
                <div>
                  <h4 className="font-semibold text-purple-900 dark:text-purple-100 mb-1">Body:</h4>
                  <p className="whitespace-pre-wrap bg-white/50 dark:bg-black/20 p-2 rounded">{videoScript.body}</p>
                </div>
                <div>
                  <h4 className="font-semibold text-purple-900 dark:text-purple-100 mb-1">Call to Action:</h4>
                  <p className="bg-white/50 dark:bg-black/20 p-2 rounded">{videoScript.callToAction}</p>
                </div>
              </div>
            </div>

            {/* Video Caption Section */}
            {videoScript.videoCaption && (
              <div className="border-b pb-4">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-bold text-lg text-purple-900 dark:text-purple-100">📱 Video Caption</h3>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      navigator.clipboard.writeText(videoScript.videoCaption || '');
                      toast({
                        title: 'Copied!',
                        description: 'Video caption copied to clipboard',
                      });
                    }}
                  >
                    Copy Caption
                  </Button>
                </div>
                <p className="text-sm bg-white/50 dark:bg-black/20 p-3 rounded">{videoScript.videoCaption}</p>
              </div>
            )}

            {/* Social Media Post Section */}
            {videoScript.socialMediaPost && (
              <div className="border-b pb-4">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-bold text-lg text-purple-900 dark:text-purple-100">📢 Social Media Post</h3>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const fullPost = `${videoScript.socialMediaPost}\n\n${videoScript.hashtags?.join(' ') || ''}`;
                      navigator.clipboard.writeText(fullPost);
                      toast({
                        title: 'Copied!',
                        description: 'Social post with hashtags copied to clipboard',
                      });
                    }}
                  >
                    Copy Post
                  </Button>
                </div>
                <p className="text-sm whitespace-pre-wrap bg-white/50 dark:bg-black/20 p-3 rounded">{videoScript.socialMediaPost}</p>
              </div>
            )}

            {/* Hashtags Section */}
            {videoScript.hashtags && videoScript.hashtags.length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-bold text-lg text-purple-900 dark:text-purple-100">#️⃣ Hashtags</h3>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      navigator.clipboard.writeText(videoScript.hashtags?.join(' ') || '');
                      toast({
                        title: 'Copied!',
                        description: 'Hashtags copied to clipboard',
                      });
                    }}
                  >
                    Copy Hashtags
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2 bg-white/50 dark:bg-black/20 p-3 rounded">
                  {videoScript.hashtags.map((tag, i) => (
                    <span key={i} className="text-xs bg-purple-200 dark:bg-purple-900 text-purple-900 dark:text-purple-100 px-2 py-1 rounded">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <Button
              className="w-full"
              onClick={() => {
                const allContent = `VIDEO SCRIPT\n${'='.repeat(50)}\nTopic: ${videoScript.topic}\n\nHook:\n${videoScript.hook}\n\nBody:\n${videoScript.body}\n\nCall to Action:\n${videoScript.callToAction}\n\n${videoScript.videoCaption ? `VIDEO CAPTION\n${'='.repeat(50)}\n${videoScript.videoCaption}\n\n` : ''}${videoScript.socialMediaPost ? `SOCIAL MEDIA POST\n${'='.repeat(50)}\n${videoScript.socialMediaPost}\n\n` : ''}${videoScript.hashtags?.length ? `HASHTAGS\n${'='.repeat(50)}\n${videoScript.hashtags.join(' ')}` : ''}`;
                navigator.clipboard.writeText(allContent);
                toast({
                  title: 'Copied!',
                  description: 'Complete content package copied to clipboard',
                });
              }}
            >
              Copy Complete Package
            </Button>
          </CardContent>
        </Card>
      )}

      <Form {...form}>
        <form className="space-y-6">
          <FormField
            control={form.control}
            name="title"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Title *</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    onChange={(e) => onTitleChange(e.target.value)}
                    placeholder="Enter blog post title"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="slug"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Slug *</FormLabel>
                <FormControl>
                  <Input {...field} placeholder="url-friendly-slug" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="excerpt"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Excerpt</FormLabel>
                <FormControl>
                  <Textarea {...field} placeholder="Brief summary..." rows={3} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="content"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Content *</FormLabel>
                <FormControl>
                  <RichTextEditor
                    content={field.value}
                    onChange={(value) => onContentChange(value)}
                    placeholder="Write your blog post content..."
                  />
                </FormControl>
                <p className="text-xs text-muted-foreground mt-1">
                  Excerpt and SEO description will be auto-generated from content if left empty
                </p>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="grid grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="author_name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Author Name</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="status"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Status</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="draft">Draft</SelectItem>
                      <SelectItem value="published">Published</SelectItem>
                      <SelectItem value="scheduled">Scheduled</SelectItem>
                      <SelectItem value="archived">Archived</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          {/* Scheduling UI - shown when status is scheduled */}
          {form.watch('status') === 'scheduled' && (
            <Card className="border-primary/20 bg-primary/5">
              <CardContent className="pt-4">
                <div className="flex items-center gap-2 mb-3">
                  <Clock className="h-4 w-4 text-primary" />
                  <span className="font-medium text-sm">Schedule Publication</span>
                </div>
                <div className="flex items-center gap-4 flex-wrap">
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-[240px] justify-start text-left font-normal",
                          !form.getValues('scheduled_publish_at') && "text-muted-foreground"
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {form.getValues('scheduled_publish_at')
                          ? format(new Date(form.getValues('scheduled_publish_at')!), 'PPP')
                          : 'Pick a publish date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={form.getValues('scheduled_publish_at') ? new Date(form.getValues('scheduled_publish_at')!) : undefined}
                        onSelect={(date) => {
                          if (date) {
                            // Preserve existing time or default to 9:00 AM
                            const existing = form.getValues('scheduled_publish_at');
                            const existingDate = existing ? new Date(existing) : null;
                            const hours = existingDate ? existingDate.getHours() : 9;
                            const mins = existingDate ? existingDate.getMinutes() : 0;
                            date.setHours(hours, mins, 0, 0);
                            form.setValue('scheduled_publish_at', date.toISOString());
                          }
                        }}
                        disabled={(d) => d < new Date()}
                        initialFocus
                        className={cn("p-3 pointer-events-auto")}
                      />
                    </PopoverContent>
                  </Popover>
                  <Input
                    type="time"
                    className="w-[140px]"
                    value={form.getValues('scheduled_publish_at')
                      ? format(new Date(form.getValues('scheduled_publish_at')!), 'HH:mm')
                      : '09:00'}
                    onChange={(e) => {
                      const [hours, mins] = e.target.value.split(':').map(Number);
                      const current = form.getValues('scheduled_publish_at');
                      const date = current ? new Date(current) : new Date();
                      date.setHours(hours, mins, 0, 0);
                      form.setValue('scheduled_publish_at', date.toISOString());
                    }}
                  />
                </div>
                {form.getValues('scheduled_publish_at') && (
                  <p className="text-sm text-muted-foreground mt-2">
                    Will be published on {format(new Date(form.getValues('scheduled_publish_at')!), 'PPP')} at {format(new Date(form.getValues('scheduled_publish_at')!), 'h:mm a')}
                  </p>
                )}
              </CardContent>
            </Card>
          )}

          <FormField
            control={form.control}
            name="featured_image_url"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Featured Image</FormLabel>
                <div className="space-y-4">
                  {/* Upload button */}
                  <div className="flex items-center gap-4">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/jpeg,image/jpg,image/png,image/webp,image/gif"
                      onChange={handleImageUpload}
                      className="hidden"
                      id="featured-image-upload"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={isUploadingImage}
                      className="flex items-center gap-2"
                    >
                      {isUploadingImage ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Uploading...
                        </>
                      ) : (
                        <>
                          <Upload className="h-4 w-4" />
                          Upload Image
                        </>
                      )}
                    </Button>
                    <span className="text-sm text-muted-foreground">
                      JPEG, PNG, WebP, or GIF (max 10MB)
                    </span>
                  </div>
                  
                  {/* Or paste URL */}
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">Or paste URL:</span>
                    <FormControl>
                      <Input 
                        {...field} 
                        placeholder="https://..." 
                        className="flex-1"
                      />
                    </FormControl>
                  </div>
                  
                  {/* Image preview */}
                  {field.value && (
                    <div className="border rounded-lg p-4 bg-muted/20">
                      <div className="flex items-start justify-between mb-2">
                        <p className="text-sm font-medium">Featured Image Preview:</p>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => form.setValue('featured_image_url', '')}
                          className="text-destructive hover:text-destructive"
                        >
                          <X className="h-4 w-4 mr-1" />
                          Remove
                        </Button>
                      </div>
                      <img 
                        src={field.value} 
                        alt="Featured preview" 
                        className="w-full max-w-2xl rounded-md shadow-sm"
                        onError={(e) => {
                          e.currentTarget.src = '/placeholder.svg';
                        }}
                      />
                    </div>
                  )}
                </div>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="featured_image_alt"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Featured Image Alt Text</FormLabel>
                <FormControl>
                  <Input {...field} placeholder="Descriptive alt text" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="categories"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Categories (comma-separated)</FormLabel>
                <FormControl>
                  <Input {...field} placeholder="Health, Wellness, Safety" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="tags"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Tags (comma-separated)</FormLabel>
                <FormControl>
                  <Input {...field} placeholder="fall prevention, home safety" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="seo_description"
            render={({ field }) => (
              <FormItem>
                <FormLabel>SEO Meta Description</FormLabel>
                <FormControl>
                  <Textarea {...field} placeholder="Brief description for search engines..." rows={2} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="flex gap-4 flex-wrap">
            <Button
              type="button"
              variant="outline"
              onClick={form.handleSubmit((data) => onSubmit(data, 'draft'))}
              disabled={isLoading}
            >
              {isLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
              Save as Draft
            </Button>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  disabled={isLoading}
                  className="border-amber-600 text-amber-700 hover:bg-amber-50"
                >
                  <Clock className="h-4 w-4 mr-2" />
                  {form.watch('scheduled_publish_at')
                    ? `Scheduled: ${format(new Date(form.getValues('scheduled_publish_at')!), 'MMM d, h:mm a')}`
                    : 'Schedule'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-4" align="start">
                <div className="space-y-3">
                  <p className="text-sm font-medium">Schedule Publication</p>
                  <Calendar
                    mode="single"
                    selected={form.getValues('scheduled_publish_at') ? new Date(form.getValues('scheduled_publish_at')!) : undefined}
                    onSelect={(date) => {
                      if (date) {
                        const existing = form.getValues('scheduled_publish_at');
                        const existingDate = existing ? new Date(existing) : null;
                        const hours = existingDate ? existingDate.getHours() : 9;
                        const mins = existingDate ? existingDate.getMinutes() : 0;
                        date.setHours(hours, mins, 0, 0);
                        form.setValue('scheduled_publish_at', date.toISOString());
                        form.setValue('status', 'scheduled');
                      }
                    }}
                    disabled={(d) => d < new Date()}
                    initialFocus
                    className={cn("p-3 pointer-events-auto")}
                  />
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">Time:</span>
                    <Input
                      type="time"
                      className="w-[140px]"
                      value={form.getValues('scheduled_publish_at')
                        ? format(new Date(form.getValues('scheduled_publish_at')!), 'HH:mm')
                        : '09:00'}
                      onChange={(e) => {
                        const [hours, mins] = e.target.value.split(':').map(Number);
                        const current = form.getValues('scheduled_publish_at');
                        const date = current ? new Date(current) : new Date();
                        date.setHours(hours, mins, 0, 0);
                        form.setValue('scheduled_publish_at', date.toISOString());
                        form.setValue('status', 'scheduled');
                      }}
                    />
                  </div>
                  {form.watch('scheduled_publish_at') && (
                    <Button
                      type="button"
                      size="sm"
                      className="w-full bg-amber-600 hover:bg-amber-700 text-white"
                      onClick={() => {
                        form.handleSubmit((data) => onSubmit(data, 'scheduled'))();
                      }}
                      disabled={isLoading}
                    >
                      {isLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Clock className="h-4 w-4 mr-2" />}
                      Confirm Schedule
                    </Button>
                  )}
                </div>
              </PopoverContent>
            </Popover>
            <Button
              type="button"
              onClick={form.handleSubmit((data) => onSubmit(data, 'published'))}
              disabled={isLoading}
            >
              {isLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
              Publish Now
            </Button>
          </div>
        </form>
      </Form>

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the blog post.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
